import type { ShallowReactive, UnwrapNestedRefs } from "vue"

import type { ArraySchema, InferOutput } from "valibot"
import { getDefault, getFallback, safeParse, unwrap } from "valibot"

import { type FieldSchemaMapping, type PossibleReturnType } from "./shared"
import { useField } from "./useField"
import { useForm } from "./useForm"

type UseArrayFieldProps<TSchema extends ArraySchema<any, any>> = {
  name: string
  schema: TSchema
  defaultValue?: MaybeRefOrGetter<InferOutput<TSchema>>
}

function generateName(name: string, index: number) {
  return `${name}[${index}]`
}

function fixFieldValue<TValue extends any = any>(value: TValue): any {
  if (value === "" || value === undefined || Object.keys(value || {}).length === 0) return null
  return value
}

export function useArrayField<TSchema extends ArraySchema<any, any>>(
  props: UseArrayFieldProps<TSchema>
): UseArrayFieldReturnType<TSchema> {
  const { name, schema: originalSchema, defaultValue = undefined } = toRefs(reactive(props))
  const schemaUnwrapped = computed((): TSchema extends { wrapped: infer U } ? U : TSchema =>
    "wrapped" in originalSchema.value ? unwrap(originalSchema.value) : originalSchema.value
  )

  const schemaDefaultValue = computed(() => getDefault(schemaUnwrapped.value))
  const schemaFallback = computed(() => getFallback(schemaUnwrapped.value))

  const fields = shallowRef<PossibleReturnType<TSchema["item"]>[]>(
    (schemaDefaultValue.value ?? schemaFallback.value ?? []) as PossibleReturnType<TSchema["item"]>[]
  )

  const state = computed<InferOutput<TSchema["item"]>[]>({
    get() {
      return fields.value.map((field) => {
        if (field.type === "field") return deepToRaw(field.state)
        return field.state
      })
    },
    set(val) {
      syncValue(val)
    },
  })

  watchOnce(
    () => [defaultValue, originalSchema],
    ([defaults]) => (state.value = defaults?.value ?? []),
    { deep: true, immediate: true }
  )

  const eagerValid = computed(() =>
    fields.value.length || defaultValue?.value !== undefined
      ? fields.value.every((field) => ("eagerValid" in field ? field.eagerValid.value : true))
      : false
  )

  const isValid = computed(() =>
    fields.value.length || defaultValue?.value !== undefined
      ? fields.value.every((field) => ("isValid" in field ? field.isValid.value : true))
      : false
  )

  const isDirty = computed(() => fields.value.some((field) => ("dirty" in field ? field.dirty.value : false)))

  const isTouched = computed(() =>
    fields.value.some((field) => ("touched" in field ? field.touched.value : false))
  )

  const errors = computed(() =>
    fields.value.flatMap((field) => ("errors" in field ? field.errors.value : []))
  )

  function validate() {
    return fields.value.every((field) => {
      field.validate?.()
      return field.isValid?.value
    })
  }

  function reset() {
    fields.value = []
    state.value = []
  }

  function createField(
    value: InferOutput<TSchema["item"]> = undefined,
    index: number
  ): PossibleReturnType<TSchema["item"]> {
    const itemSchema = originalSchema.value.item
    const unwrappedItemSchema = schemaUnwrapped.value.item

    if ("entries" in unwrappedItemSchema) {
      return useForm<TSchema["item"]>(itemSchema, { defaultValues: value })
    } else {
      return useField({
        name: generateName(name.value, index),
        schema: itemSchema,
        defaultValue: value,
      })
    }
  }

  function addField(value?: InferOutput<TSchema["item"]>) {
    fields.value = [...fields.value, createField(value, fields.value.length)]
  }

  function removeField(index: number) {
    fields.value = fields.value.filter((_, i) => i !== index)
    state.value = state.value.filter((_, i) => i !== index)
  }

  function syncValue(value: InferOutput<TSchema>[]) {
    const currentLength = fields.value.length
    const newLength = value?.length || 0

    if (newLength > currentLength) {
      for (let i = currentLength; i < newLength; i++) {
        addField(value[i])
      }
    } else if (newLength < currentLength) {
      for (let i = currentLength - 1; i >= newLength; i--) {
        removeField(i)
      }
    }

    if (Array.isArray(value)) {
      value.forEach((val, index) => {
        const field = fields.value[index]
        if (field) {
          if ("setValues" in field) {
            field.setValues(val)
          } else if ("setState" in field) {
            field.setState(val)
          }
        }
      })
    } else {
      fields.value = []
    }
  }

  function setState(newState: InferOutput<TSchema["item"]>[]): void {
    state.value = toValue(newState)
  }

  return {
    type: "array",
    fieldName: name.value,

    state,
    fields: reactiveComputed(() => fields.value as FieldSchemaMapping<TSchema>[]),
    eagerValid,
    isValid,
    isDirty,
    isTouched,
    errors,
    validate,
    reset,
    addField,
    removeField,
    syncValue,
    setState,
  }
}

export type UseArrayFieldReturnType<TSchema extends ArraySchema<any, any>> = {
  /**
   * The type of the form.
   */
  type: "array"

  /**
   * Field name
   */
  fieldName: string

  state: Ref<InferOutput<TSchema["item"]>[]>
  fields: UnwrapNestedRefs<FieldSchemaMapping<TSchema>[]>
  eagerValid: ComputedRef<boolean>
  isValid: ComputedRef<boolean>
  isDirty: ComputedRef<boolean>
  isTouched: ComputedRef<boolean>
  errors: ComputedRef<string[]>
  validate: () => boolean
  reset: () => void
  addField: (value?: InferOutput<TSchema["item"]>) => void
  removeField: (index: number) => void
  syncValue: (value: InferOutput<TSchema>) => void
  setState: (value: InferOutput<TSchema>[]) => void
}
